<template>
  <v-dialog
    v-if="beginApprovalBasedEditDialog"
    v-model="beginApprovalBasedEditDialog"
    persistent
    width="400px"
  >
    <ConfirmDialog
      :title="confirmDialogConfig.title"
      :body="confirmDialogConfig.body"
      color="primary"
      :confirmText="confirmDialogConfig.confirmText"
      :canCancel="confirmDialogConfig.canCancel"
      @confirm="confirmDialogConfig.confirmAction"
      @cancel="confirmDialogConfig.cancelAction"
    />
  </v-dialog>
  <v-card v-else-if="beginApprovalBasedEditDialog === false" :id="getContainerId">
    <input
      ref="uploader"
      class="d-none"
      type="file"
      @change="fileChanged"/>
    <v-card-title :class="contractColor">
      <span class="headline mr-4 white--text">{{title}}</span>
      <ActivityFormContractHeader v-if="activeTab !== 0" :contract="contract" />
      <v-spacer />
      <v-menu bottom left v-if="destinationAccount?.isExternal">
        <template #activator="{on}">
          <v-icon v-on="on" color="tertiary">mdi-dots-vertical</v-icon>
        </template>
        <v-list>
          <v-list-item v-for="(ha, index) in headerActions" :key="`${ha.text}-${index}`" @click="ha.action()" :disabled="ha.disabled" :id="`contract-edit-context-menu-${kebabCase(ha.text)}`">
            <v-avatar left>
              <v-icon color="black" :disabled="ha.disabled">{{ ha.icon }}</v-icon>
            </v-avatar>
            <v-list-item-title class="subtitle-1">{{ $t(ha.text) }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <BaseDialogActions
        :hideCancel="contractRequiresEditApproval"
        hideRefresh
      />
    </v-card-title>
    <v-card-text>
      <v-tabs
        v-model="activeTab"
        class="mt-4"
      >
        <v-tab data-testid="contract-info-tab">{{ $t("contractInfo") }}</v-tab>
        <v-tab>{{ $t('products') }}</v-tab>
        <v-tab data-testid="activities-tab" :key="activitiesTabKey">
          <span>
            {{ $t("activities") }}
            <Icon
            v-if="activityIssue"
            icon="mdi-alert-circle"
            iconColor="error"
            :small="true"
            :tooltipText="activityIssue"/>
          </span>
        </v-tab>
        <v-tab-item disabled>
          <FormWrapper
            class="pb-1"
            v-show="activeTab === 0"
            testId="save-contract"
            strongValidation
            :buttonText="contractRequiresEditApproval ? $t('finishEditingContract') : undefined"
            :disabled="isButtonDisabled || missingTractRequirements.length > 0"
            :buttonColor="contractColor"
            @submit="trySave"
            buttonId="save-contract"
          >
            <v-container
              fluid
              class="mt-10"
              data-testid="contract-form"
            >
              <v-row dense>
                <v-col v-if="contractHasTract" cols="12" lg="6">
                  <LoadableComponent :loading="loadingRefreshing">
                    <TractAutocomplete
                      :propTractId="contract.tractId"
                      :propFetchTracts="false"
                      @tract-chosen="tractChosen"
                      data-testid="contract-tract"
                    />
                    <v-alert
                      v-if="missingTractRequirements.length > 0"
                      type="warning"
                      outlined
                      text
                      dense
                      data-testid="contract-tract-missing-requirements">
                      {{ $t('tractMissingContractRequirements', { fields: requiredFieldMessages(missingTractRequirements) }) }}
                    </v-alert>
                  </LoadableComponent>
                </v-col>
                <v-col cols="12" lg="6">
                  <LoadableComponent :loading="loadingRefreshing">
                    <AccountAutocomplete
                      :accountId="contract.accountId"
                      @account-chosen="accountChosen"
                      userSetting="contractAccount"
                      elementId="contract-account"
                      showCertified
                      :fetchTagsAndAccounts="false"
                      :ticketBookAccounts="ticketBookAccounts"
                      :nonTicketBookAccountTooltip="nonTicketBookAccountTooltip"
                    />
                  </LoadableComponent>
                </v-col>
                <v-col v-if="contractHasFromAccount" cols="12" lg="6">
                  <LoadableComponent :loading="loadingRefreshing">
                    <AccountAutocomplete
                      :onlyInternal="fromAccountIsInternal"
                      :onlyExternal="fromAccountIsExternal"
                      :accountId="contract.fromAccountId"
                      :title="$t('fromAccount')"
                      @account-chosen="fromAccountChosen"
                      elementId="contract-from-account"
                      userSetting="contractFromAccount"
                      :fetchTagsAndAccounts="false"
                      :itemDisabled="currentContractType === ContractType.ByproductHauling ? invalidAccountReceiverSpot : undefined"
                      :disabledItemTooltip="$t('accountsWithoutReceiverLocation')"
                    />
                  </LoadableComponent>
                </v-col>
                <v-col cols="12" lg="6">
                  <LoadableComponent :loading="loadingRefreshing">
                    <AccountAutocomplete
                      userSetting="contractDestinationAccount"
                      :accountId="contract.destinationAccountId"
                      :title="$t('destinationAccount')"
                      @account-chosen="destinationChosen"
                      :onlyInternal="contractDestinationMustBeInternal"
                      :onlyExternal="contractDestinationMustBeExternal"
                      elementId="contract-destination"
                      :fetchTagsAndAccounts="false"
                      :propAccounts="tractDestinations ?? undefined"
                      :propLoading="tractDestinationsLoading"
                      :disabled="tractDestinations?.length === 0"
                      :hint="tractDestinations?.length === 0 ? $t('noTractDestinations') : undefined"
                      :noSelection="tractDestinations?.length === 0"
                      :itemDisabled="currentContractType === ContractType.ByproductHauling ? invalidAccountReceiverSpot : undefined"
                      :disabledItemTooltip="$t('accountsWithoutReceiverLocation')"
                    /> <!-- "No tract destinations" is a safeguard, it shouldn't happen in practice -->
                  </LoadableComponent>
                </v-col>
                <v-col v-if="contractMode.value === ContractMode.Logs.value" cols="12" lg="6">
                  <LoadableComponent :loading="loadingRefreshing">
                    <SettingAutocomplete
                      :settingId="contract.settingId"
                      :propFetchSettings="false"
                      @setting-chosen="settingChosen"
                      elementId="contract-setting"
                    />
                  </LoadableComponent>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col lg="12">
                  <LoadableComponent :loading="loadingRefreshing">
                    <ContractInfo
                      :propContractInfo="contractInformation"
                      :editing="true"
                      :contractMode="contractMode"
                      :destinationAccountId="updatedContract.destinationAccountId"
                      :contractId="contract.contractId"
                      :hideDraft="!currentDraftMode && companyRequiresEditApproval"
                      :hasVolumeActivity="someVolumeActivityExists"
                      @contract-info-changed="contractInfoChanged"
                      @is-button-disabled="(val) => isButtonDisabled = val"
                      :contractType="currentContractType"
                    />
                  </LoadableComponent>
                </v-col>
              </v-row>
            </v-container>
            <template #left-action>
              <v-btn text color="black" outlined @click="refreshContract(contractId)">{{$t('reset')}}</v-btn>
            </template>
          </FormWrapper>
        </v-tab-item>
        <v-tab-item>
          <v-row class="mt-2">
            <v-col cols="12" md="6">
              <v-card outlined style="min-height: 300px;" class="px-4 py-4">
                <v-row>
                  <v-col cols="12">
                    <span class="subtitle-1">
                      {{ $t('contractProducts') }}
                    </span>
                    <Icon
                    icon="mdi-pencil"
                    :tooltipText="$t('editProducts')"
                    @icon-clicked="openDialog"/>
                  </v-col>
                </v-row>
                <v-row class="mt-n1">
                  <v-col cols="12">
                    <v-data-table
                    dense
                    :items-per-page="-1"
                    hide-default-footer
                    :items="sortedProducts"
                    :headers="productTableHeaders"
                    :no-data-text="$t('noProductsSpecified')">
                      <template #item.scaleFrequency="{ item }">
                        <Icon
                          v-if="getDisplayFrequencyForTooltip(item) !== null"
                          icon="mdi-crosshairs-gps"
                          :small="false"
                          iconColor="black"
                          :tooltipText="$t('destinationScaleFrequency', { frequency: getDisplayFrequencyForTooltip(item), tractDestination: destinationAccount?.name })"
                        />
                        <span v-if="item.scaleFrequency !== null && item.scaleFrequency > 0" class="body-1">
                          {{ convertPercentToRatio(item.scaleFrequency) }}
                        </span>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
            <v-col align="right" cols="12">
              <v-spacer/>
              <v-btn :class="contractColor" @click="trySave">
                {{ $t('finishEditingContract') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-tab-item>
        <v-tab-item disabled>
          <LoadableComponent :loading="loadingRefreshing">
            <Activities
              :contract="contract"
              :tract="tract"
              :showFinishButton="contractRequiresEditApproval"
              @refreshed="triggerActivitiesTabChange"
              @finish-editing="trySave"
              @refresh-contract="onRateChangeSavedOrDeleted(contractId)"
            />
          </LoadableComponent>
        </v-tab-item>
        <v-tab-item disabled>
          <GatePass/>
        </v-tab-item>
      </v-tabs>
    </v-card-text>
    <v-dialog
      v-if="confirmSubmitDialog"
      v-model="confirmSubmitDialog"
      width="400px"
    >
      <ConfirmDialog
        :title="$t('confirmSubmitContractTitle')"
        :body="this.currentDraftMode ? $t('confirmSubmitDraftContractMessage') : $t('confirmSubmitContractMessage')"
        color="primary"
        :confirmText="$t('submit')"
        @confirm="save"
        @cancel="confirmSubmitDialog = false"
      >
        <template #custom-body>
          <v-textarea
          v-model="contractApprovalNote"
          :label="$t('note')"
          outlined
          auto-grow
          counter
          maxlength="1024"
          clearable
          dense
          color="black"
          />
        </template>
      </ConfirmDialog>
    </v-dialog>
    <Dialog  :stateId="dialogId" @dialog-closing="closeDialogs">
      <ContractProductForm
      @close="closeContractProductForm"
      :contract="contract"
      :products="sortedProducts"
      :activities="allActivities"/>
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { utcToLocalDate, localToUTC } from '@/utils/DateFormatter.js'
import { gatePassClient } from '@/utils/GatePass'
import { saveAs } from 'file-saver'
import { colorClassForContractType } from '@/utils/componentHelpers.js'
import { AccountingCategory, PayBy, ContractApprovalStatus, ContractType, ContractMode, TemplateSpecialization } from '@/utils/Enumerations.js'
import { kebabCase, cloneDeep } from 'lodash'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { requiredFieldsForTractType, missingRequiredFields, requiredFieldMessages } from '@/utils/TractRequirements.js'

export default {
  name: 'ContractEditor',

  components: {
    ContractInfo: () => import('@/components/contract/ContractInfo'),
    Activities: () => import('@/components/activity/Activities'),
    TractAutocomplete: () => import('@/components/autocomplete/TractAutocomplete.vue'),
    SettingAutocomplete: () => import('@/components/autocomplete/ContractSettingAutocomplete.vue'),
    AccountAutocomplete: () => import('@/components/autocomplete/AccountAutocomplete.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    ActivityFormContractHeader: () => import('@/components/activity/ActivityFormContractHeader.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    ContractProductForm: () => import('@/components/contract/contract-detail/ContractProductForm.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue')
  },

  props: {
    contractId: { type: Number, required: true },
    shouldAddActivity: { type: Boolean, default: false },
    editProducts: { type: Boolean, default: false },
    isNested: { type: Boolean, default: false },
    contractMode: { type: Object, required: true },
    companyRequiresEditApproval: { type: Boolean, required: true },
    currentDraftMode: { type: Boolean, required: false, default: false }
  },

  data: () => ({
    dialogId: uniqueDialogId('contract-editor-product-dialog'),
    ContractMode,
    ContractType,
    loading: true,
    refreshing: false,
    activeTab: 0,
    isButtonDisabled: false,
    activitiesTabKey: 0,
    destinationAccount: null,
    contractInformation: {
      effectiveDate: '',
      expirationDate: '',
      acceptsAnyLoad: false,
      requiresLogger: false,
      requiresExt1: false,
      requiresExt2: false,
      isDraft: false,
      paused: false,
      type: '',
      distance: 0,
      tareWeight: 0,
      allowPickupLoads: false,
      ticketBookIds: []
    },
    contract: undefined,
    updatedContract: {
      accountId: null,
      fromAccountId: null,
      tractId: null,
      settingId: null,
      destinationAccountId: null,
      acceptsAnyLoad: false,
      requiresExt1: false,
      requiresExt2: false,
      isDraft: false,
      paused: false,
      type: '',
      distance: 0,
      expirationDate: '',
      effectiveDate: '',
      allowPickupLoads: false
    },
    initContractType: undefined,
    gatePass: undefined,
    isSelecting: false,
    allowedGatePassMimes: undefined,
    approval: undefined,
    confirmSubmitDialog: false,
    contractApprovalId: null,
    beginApprovalBasedEditDialog: null,
    contractApprovalNote: '',
    tractDestinations: undefined,
    tractDestinationsLoading: false,
    tractTicketBookAccountIds: [],
    tract: null,
    tractAutocompleteLoading: false,
    productDialog: false,
    contractProducts: [],
    tractDestinationProducts: []
  }),

  computed: {
    ...mapGetters('activity', ['allActivities']),
    ...mapGetters('user', ['userInfo']),
    ...mapGetters('user-settings', ['mutatedUserSettings']),
    ...mapGetters('account', ['allAccounts']),
    ...mapGetters('tract-type', ['allTractTypes']),

    missingTractRequirements () {
      if (!this.tract || this.updatedContract.tractId === this.contract.tractId) return []
      const tractType = this.allTractTypes.find(tt => tt.tractTypeId === this.tract.type?.tractTypeId)
      return missingRequiredFields(this.tract, requiredFieldsForTractType(tractType))
    },

    contractHasFromAccount () {
      const modesWithFromAccount = [ContractMode.Transfers.value, ContractMode.Byproducts.value, ContractMode.LogYardSale.value]
      return this.isByproductPurchase
        ? !!this.contract.fromAccount
        : modesWithFromAccount.includes(this.contractMode.value)
    },

    loadingRefreshing () {
      return this.loading || this.refreshing
    },

    contractHasTract () {
      return this.contract.tractId && [
        ContractType.WoodsSale.value,
        ContractType.Production.value,
        ContractType.ByproductPurchase.value
      ].includes(this.currentContractType.value)
    },

    fromAccountIsInternal () {
      return this.contractMode === ContractType.ByproductSale ||
        this.contractMode === ContractType.Transfer ||
        this.contractMode === ContractType.LogYardSale
    },

    fromAccountIsExternal () {
      return this.contract.type === ContractType.ByproductHauling.value
    },

    isByproductPurchase () {
      return this.contract.type === ContractType.ByproductPurchase.value
    },

    contractDestinationMustBeInternal () {
      return this.contractMode.value === ContractMode.Transfers.value || this.isByproductPurchase
    },

    contractDestinationMustBeExternal () {
      return this.contractMode.value === ContractMode.LogYardSale.value || this.contract.type === ContractType.ByproductSale.value || this.contract.type === ContractType.ByproductHauling.value
    },

    productTableHeaders () {
      var result = [{ text: this.$t('name'), value: 'name' }]
      if (this.contractProducts.some(p => p.scaleFrequency !== null && p.scaleFrequency > 0) ||
        this.tractDestinationProducts.some(p => p.scaleFrequency !== null && p.scaleFrequency > 0)) {
        result.push({ text: this.$t('scaleFrequency'), value: 'scaleFrequency', align: 'right' })
      }
      return result
    },

    title () {
      switch (this.contractMode.value) {
        case ContractMode.Logs.value:
          return this.$t('editingLogsContract')
        case ContractMode.Byproducts.value:
          return this.$t('editingByproductContract')
        case ContractMode.Transfers.value:
          return this.$t('editingTransferContract')
        case ContractMode.LogYardSale.value:
          return this.$t('editingLogYardSaleContract')
      }

      return ''
    },

    contractColor () {
      return colorClassForContractType(this.contractInformation.type, false)
    },

    headerActions () {
      return [
        {
          text: 'uploadGatePass',
          icon: 'mdi-file-upload-outline',
          action: this.uploadGatePass,
          disabled: this.gatePass !== undefined
        },
        {
          text: 'downloadGatePass',
          icon: 'mdi-file-download-outline',
          action: this.downloadGatePass,
          disabled: this.gatePass === undefined
        },
        {
          text: 'deleteGatePass',
          icon: 'mdi-file-document-remove-outline',
          action: this.deleteGatePass,
          disabled: this.gatePass === undefined
        }
      ]
    },

    someTransferActivityExists () {
      return this.allActivities.some(a => a.activityTemplate?.specialization === TemplateSpecialization.Transfer.value) || this.allActivities.some(a => a.baseCost?.payBy === PayBy.Volume.value)
    },

    someVolumeActivityExists () {
      return this.allActivities.some(a => a.baseCost?.payBy === PayBy.Volume.value)
    },

    somePayableActivityExists () {
      return this.allActivities.some(a => a.activityTemplate?.category === AccountingCategory.Payable.value)
    },

    someReceivableActivityExists () {
      return this.allActivities.some(a => a.activityTemplate?.category === AccountingCategory.Receivable.value)
    },

    activityIssue () {
      if (this.contract.type === ContractType.ByproductPurchase.value && !this.somePayableActivityExists) return this.$t('missingAccountsPayableActivity')
      if ((this.contract.type === ContractType.WoodsSale.value || this.contract.type === ContractType.LogYardSale.value) && !this.someReceivableActivityExists) return this.$t('missingAccountsReceivableActivity')
      if (this.contract.type === ContractType.Transfer.value && !this.someTransferActivityExists) return this.$t('missingTransferActivity')
      return ''
    },

    canEditContract () {
      return [ContractApprovalStatus.Approved.value, ContractApprovalStatus.Returned.value, null].includes(this.contract.approvalStatus)
    },

    resumingPreviousModification () {
      return this.contract.approvalStatus === ContractApprovalStatus.PendingModification.value
        ? this.approval.requestedByUserId === this.userInfo.applicationUserId
        : false
    },

    contractRequiresEditApproval () {
      return this.companyRequiresEditApproval && !this.currentDraftMode
    },

    confirmDialogConfig () {
      if (this.resumingPreviousModification) {
        return {
          title: this.$t('resumeEditContractConfirmTitle'),
          body: this.$t('resumeEditContractConfirmMessage'),
          confirmText: this.$t('edit'),
          canCancel: true,
          confirmAction: this.resumeApprovalBasedEdit,
          cancelAction: () => this.$emit('close', { refresh: false })
        }
      } else if (this.canEditContract) {
        return {
          title: this.$t('contractEditConfirmTitle'),
          body: this.$t('contractEditConfirmMessage'),
          confirmText: this.$t('edit'),
          canCancel: true,
          confirmAction: this.beginApprovalBasedEdit,
          cancelAction: () => this.$emit('close', { refresh: false })
        }
      } else {
        return {
          title: this.$t('cannotEditContractTitle'),
          body: this.$t('cannotEditContractMessage', { status: ContractApprovalStatus.fromInt(this.contract.approvalStatus) }),
          confirmText: this.$t('okay'),
          canCancel: false,
          confirmAction: () => this.$emit('close'),
          cancelAction: () => undefined
        }
      }
    },

    currentContractType () {
      return ContractType.forInt(this.contract?.type)
    },

    getContainerId () {
      return `contract-editor-${kebabCase(ContractMode.labelFromInt(this.contractMode.value))}`
    },

    sortedProducts () {
      return this.contractProducts?.toSorted((a, b) => a.sortOrder - b.sortOrder) || []
    },

    contractTicketBookAccountId () {
      return this.contractInformation.ticketBookIds?.length > 0 ? this.updatedContract.destinationAccountId ?? null : null
    },

    ticketBookAccounts () {
      const accounts = this.tractTicketBookAccountIds.map(accountId => ({ accountId, tooltipText: this.$t('tractTicketBookAccount') }))
      if (this.contractTicketBookAccountId != null) {
        accounts.push({ accountId: this.contractTicketBookAccountId, tooltipText: this.$t('contractTicketBookAccount') })
      }
      return accounts
    },

    nonTicketBookAccountTooltip () {
      if (!this.contractHasTract || this.destinationAccount?.isExternal !== false) return undefined
      const hasTractTicketBooks = this.tractTicketBookAccountIds.length > 0
      const hasContractTicketBook = this.contractTicketBookAccountId != null
      if (hasTractTicketBooks && hasContractTicketBook) return this.$t('notTractOrContractTicketBookAccount')
      return hasContractTicketBook ? this.$t('notContractTicketBookAccount') : this.$t('notTractTicketBookAccount')
    }
  },

  async created () {
    this.loading = true
    try {
      await Promise.all([
        this.getAccounts(),
        this.getAccountTags(),
        this.fetchSettings(),
        ...(this.contractMode.value === ContractMode.Logs.value
          ? [this.fetchTracts(), this.refreshContract(this.contractId)]
          : [this.refreshContract(this.contractId)]),
        this.getGatePass(),
        this.allowedGatePassMimes = new Set([
          'application/pdf',
          'image/png',
          'image/jpeg',
          'image/heic'
        ])
      ])
      if (this.contract.approvalStatus === ContractApprovalStatus.PendingModification.value) {
        this.approval = await this.getMostRecentContractApproval(this.contractId)
      }
      this.beginApprovalBasedEditDialog = this.contractRequiresEditApproval
      if (this.editProducts) {
        this.activeTab = 1
        requestAnimationFrame(_ => { this.openDialog() })
      } else if (this.shouldAddActivity) {
        this.activeTab = 2
      }
    } finally {
      this.loading = false
    }
  },

  beforeDestroy () {
    this.updateUserSettings(this.mutatedUserSettings)
    this.$emit('close')
  },

  methods: {
    kebabCase,
    ...mapActions('activity', ['fetchActivitiesByEntity']),
    ...mapActions('contract', ['updateContract', 'getContract']),
    ...mapActions('contract-approvals', ['createContractApproval', 'updateContractApproval', 'getMostRecentContractApproval']),
    ...mapActions('user-settings', ['updateUserSettings']),
    ...mapMutations('snackbar', ['setSnack']),
    ...mapActions('account', ['fetchAccounts']),
    ...mapActions('tags', ['fetchAccountTags']),
    ...mapActions('tract-destination', ['fetchTractDestinations']),
    ...mapActions('tract-type', ['fetchTractTypes']),
    requiredFieldMessages,
    ...mapActions('setting', ['fetchSettings']),
    ...mapActions('tract', ['fetchTracts', 'getTractWithId']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    async refreshContract (contractId) {
      this.refreshing = true
      try {
        const res = await this.getContract(contractId)
        this.contract = cloneDeep(res)
        this.contractInformation = {
          effectiveDate: utcToLocalDate(this.contract.effectiveDate),
          expirationDate: utcToLocalDate(this.contract.expirationDate),
          acceptsAnyLoad: this.contract.acceptsAnyLoad,
          requiresLogger: this.contract.requiresLogger,
          requiresExt1: this.contract.requiresExt1,
          requiresExt2: this.contract.requiresExt2,
          isDraft: this.contract.isDraft,
          paused: this.contract.paused,
          type: this.contract.type,
          distance: this.contract.distance ? this.contract.distance : 0,
          tareWeight: this.contract.tareWeight ? this.contract.tareWeight : 0,
          allowPickupLoads: this.contract.allowPickupLoads,
          ticketBookIds: this.contract.ticketBooks?.map(tb => tb.ticketBookId) ?? []
        }
        this.tractChosen(this.contract)
        this.initContractType = this.selectedContractType
        this.fetchActivitiesByEntity(this.contract.contractId)
        this.contractProducts = this.contract.products
        this.initializeUpdatedContract(this.contract)
      } finally {
        this.refreshing = false
      }
    },

    async getAccounts () {
      await this.fetchAccounts()
    },

    async getAccountTags () {
      await this.fetchAccountTags()
    },

    contractInfoChanged (contractInfo) {
      this.contractInformation = { ...contractInfo }
    },

    async tractChosen (val) {
      this.updatedContract.tractId = val?.tractId
      if (this.updatedContract.tractId) {
        this.tractDestinationsLoading = true
        const [response, tract] = await Promise.all([
          this.fetchTractDestinations(val?.tractId),
          this.getTractWithId(val?.tractId),
          this.allTractTypes.length === 0 ? this.fetchTractTypes() : undefined
        ])
        this.tract = tract
        this.tractTicketBookAccountIds = tract.ticketBooks?.map(tb => tb.accountId) ?? []
        this.tractDestinationProducts = response.find(td => td.destinationAccountId === this.updatedContract.destinationAccountId)?.products || []
        this.tractDestinations = response.map(td => JSON.parse(JSON.stringify(this.allAccounts.find(a => a.accountId === td.destinationAccountId))))
        this.tractDestinationsLoading = false
      } else {
        this.tract = null
      }
    },

    accountChosen (val) { this.updatedContract.accountId = val?.accountId },
    fromAccountChosen (val) { this.updatedContract.fromAccountId = val?.accountId },
    settingChosen (val) { this.updatedContract.settingId = val?.settingId },

    destinationChosen (val) {
      this.destinationAccount = val
      this.updatedContract.destinationAccountId = val?.accountId
    },

    trySave () {
      if (this.companyRequiresEditApproval && !this.contractInformation.isDraft) {
        this.confirmSubmitDialog = true
      } else {
        this.save()
      }
    },

    updateApproval () {
      const requestObj = {
        contractApprovalId: this.contractApprovalId,
        contractId: this.contractId,
        status: ContractApprovalStatus.PendingReview.value,
        note: this.contractApprovalNote.trim()?.length > 0 ? { content: this.contractApprovalNote } : null
      }
      this.updateContractApproval(requestObj)
    },

    validateContract (contract) {
      if (!contract.accountId) {
        this.setSnackError(this.$t('accountRequired'))
        return false
      }
      if (!contract.destinationAccountId) {
        this.setSnackError(this.$t('destinationRequired'))
        return false
      }
      if (contract.type === ContractType.ByproductSale.value && this.destinationAccount?.isExternal === false) {
        this.setSnackError(this.$t('byproductSaleContractCannotBeInternal'))
        return
      }
      if (this.isByproductPurchase && this.destinationAccount?.isExternal === true) {
        this.setSnackError(this.$t('byproductPurchaseContractCannotBeInternal'))
        return
      }
      if (this.contractHasFromAccount && !contract.fromAccountId) {
        this.setSnackError(this.$t('fromAccountRequired'))
        return false
      }
      if (this.contractHasTract && !contract.tractId) {
        this.setSnackError(this.$t('tractRequired'))
        return false
      }
      if (this.contractMode.value === ContractMode.Logs.value && !contract.settingId) {
        this.setSnackError(this.$t('settingRequired'))
        return false
      }
      if (contract.distance < 0) {
        this.setSnackError(this.$t('distanceCannotBeNegative'))
        return false
      }
      if (contract.tareWeight < 0) {
        this.setSnackError(this.$t('tareWeightCannotBeNegative'))
        return false
      }
      return true
    },

    async save () {
      const updatedContract = this.getUpdatedContract()

      if (!this.validateContract(updatedContract)) return

      let response = {}
      if (this.updatedContract.accountId && this.updatedContract.destinationAccountId) {
        if (!this.destinationAccount?.isExternal && this.gatePass) this.deleteGatePass(this.gatePass)
        response = await this.updateContract(updatedContract)
        this.updatedContract = updatedContract
      } // else we're just editing activities and never opened the contract info tab

      if (response === undefined) {
        this.confirmSubmitDialog = false
        return
      }

      if (this.companyRequiresEditApproval && !this.contractInformation.isDraft && !this.currentDraftMode) {
        this.updateApproval()
      }

      this.$emit('contract-changed')
    },

    getUpdatedContract () {
      const updatedContract = {
        accountId: this.updatedContract.accountId,
        destinationAccountId: this.updatedContract.destinationAccountId,
        fromAccountId: this.updatedContract.fromAccountId,
        effectiveDate: localToUTC(this.contractInformation.effectiveDate),
        expirationDate: localToUTC(this.contractInformation.expirationDate),
        requiresLogger: this.contractInformation.requiresLogger,
        acceptsAnyLoad: this.contractInformation.acceptsAnyLoad,
        requiresExt1: this.contractInformation.requiresExt1,
        requiresExt2: this.contractInformation.requiresExt2,
        isDraft: this.contractInformation.isDraft,
        paused: this.contractInformation.paused,
        type: this.contractInformation.type,
        distance: parseInt(this.contractInformation.distance),
        tractId: this.updatedContract.tractId,
        settingId: this.updatedContract.settingId,
        tareWeight: this.updatedContract.tareWeight,
        contractId: this.contract.contractId,
        allowPickupLoads: this.contractInformation.allowPickupLoads,
        ticketBookIds: this.contractInformation.ticketBookIds
      }

      if (!this.contractHasTract) updatedContract.tractId = null
      if (this.contractMode.value !== ContractMode.Logs.value) {
        updatedContract.settingId = null
        updatedContract.tareWeight = parseInt(this.contractInformation.tareWeight)
      } else updatedContract.tareWeight = null

      return updatedContract
    },

    triggerActivitiesTabChange () {
      this.activitiesTabKey += (this.activitiesTabKey % 2 !== 0) ? -1 : 1
      this.$emit('contract-changed-keep-open')
    },

    async onRateChangeSavedOrDeleted (contractId) {
      await this.refreshContract(contractId)
      this.$emit('contract-changed-keep-open')
    },

    uploadGatePass () {
      this.isSelecting = true
      window.addEventListener('focus', () => {
        this.isSelecting = false
      }, { once: true })

      this.$refs.uploader.click()
    },

    fileChanged (e) {
      this.gatePass = e.target.files[0]
      this.uploadToBlob()
      this.isSelecting = false
      this.$refs.uploader.value = ''
    },

    async uploadToBlob () {
      try {
        if (!this.gatePass) return
        if (!this.validateGatePass(this.gatePass)) return
        await gatePassClient.uploadGatePass(this.contract, this.gatePass)
        this.setSnack(this.$t('gatepassUploadedSuccess'))
      } catch (e) {
        this.setSnackError(this.$t('gatepassUploadError'))
        this.gatePass = undefined
      }
    },

    async downloadGatePass () {
      if (!this.gatePass) return
      if (typeof this.gatePass !== 'object') {
        const fetchedFile = await fetch(this.gatePass)
        const blob = await fetchedFile.blob()
        saveAs(blob, decodeURIComponent(fetchedFile.url.split('?')[0].split('/').pop()))
      } else {
        saveAs(this.gatePass, this.gatePass.name)
      }
    },

    async deleteGatePass () {
      try {
        await this.getGatePass()
        await gatePassClient.deleteGatePass(this.gatePass)
        this.gatePass = undefined
        this.setSnack(this.$t('gatepassDeleteSuccess'))
      } catch (e) {
        this.setSnackError(this.$t('gatepassDeleteError'))
      }
    },

    async getGatePass () {
      try {
        this.gatePass = await gatePassClient.getGatePass(this.contractId)
      } catch (e) {
        console.error(this.$t('gatepassFetchError'))
      }
    },

    validateGatePass (gatePass) {
      const limit = 1000 * 1000 // 1MB
      if (gatePass.size > limit) {
        this.setSnackError(this.$t('fileTooLargeSnack', { limit: '1MB' }))
        this.gatePass = undefined
        return false
      }
      if (!this.allowedGatePassMimes.has(gatePass.type)) {
        this.setSnackError(this.$t('fileMimeNotAllowedSnack'))
        this.gatePass = undefined
        return false
      }
      return true
    },

    async beginApprovalBasedEdit () {
      const response = await this.createContractApproval({ contractId: this.contractId })
      if (!response) return
      this.contractApprovalId = response.contractApprovalId
      this.beginApprovalBasedEditDialog = false
    },

    resumeApprovalBasedEdit () {
      this.contractApprovalId = this.approval.contractApprovalId
      this.beginApprovalBasedEditDialog = false
    },

    invalidAccountReceiverSpot (account) {
      return account.receiverSpotLatitude === null || account.receiverSpotLongitude === null
    },

    closeDialogs () {
      this.productDialog = false
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    async closeContractProductForm (products) {
      if (products) this.contractProducts = products
      await this.refreshContract(this.contractId)
      this.closeDialogs()
    },

    openDialog () {
      this.closeDialogs()
      this.productDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '400' })
    },

    initializeUpdatedContract (contract) {
      this.updatedContract.accountId = contract.accountId
      this.updatedContract.destinationAccountId = contract.destinationAccountId
      this.updatedContract.fromAccountId = contract.fromAccountId || null
      this.updatedContract.tractId = contract.tractId || null
      this.updatedContract.settingId = contract.settingId || null
      this.updatedContract.acceptsAnyLoad = contract.acceptsAnyLoad
      this.updatedContract.requiresExt1 = contract.requiresExt1
      this.updatedContract.requiresExt2 = contract.requiresExt2
      this.updatedContract.isDraft = contract.isDraft
      this.updatedContract.paused = contract.paused
      this.updatedContract.type = contract.type
      this.updatedContract.distance = contract.distance
      this.updatedContract.expirationDate = localToUTC(contract.expirationDate)
      this.updatedContract.effectiveDate = localToUTC(contract.effectiveDate)
      this.updatedContract.allowPickupLoads = contract.allowPickupLoads
    },

    getDisplayFrequencyForTooltip ({ productId, scaleFrequency }) {
      const product = this.tractDestinationProducts?.find(p => p.productId === productId)

      if (product == null || product.scaleFrequency <= 0 || scaleFrequency > 0) return null

      return this.convertPercentToRatio(product.scaleFrequency)
    },

    convertPercentToRatio (percent) {
      const p = parseFloat(percent)
      if (!isFinite(p) || p <= 0) return ''
      const denom = Math.round(100 / p)
      return `1:${denom}`
    }
  }
}
</script>
